
import useConfirmService from "@/composables/useConfirmService";
import useError from "@/composables/useError";
import useToastService from "@/composables/useToastService";
import { ApplicationType } from "@/graphql/types";
import useVuelidate from "@vuelidate/core";
import { email, helpers, required } from "@vuelidate/validators";
import { computed, defineComponent, reactive, ref, nextTick, watch } from "vue";
import { useRouter } from "vue-router";
import VDButton from "../../VDButton.vue";
import useCreateApplication from "./mutations/useCreateApplication";
import useDeleteApplication from "./mutations/useDeleteApplication";
import useUpdateApplication from "./mutations/useUpdateApplication";
import FormErrors from "@/forms/FormErrors.vue";

export default defineComponent({
  components: { VDButton, FormErrors },
  setup() {
    const toastService = useToastService();
    const confirmService = useConfirmService();
    const router = useRouter();

    const isVisible = ref(false);
    const nameInput = ref();
    const application: ApplicationType = reactive({});
    const v$ = useVuelidate(useValidations(), application);

    const createMutation = useCreateApplication();
    const updateMutation = useUpdateApplication();
    const deleteMutation = useDeleteApplication();

    const newEmail = ref<string>("");
    const isEmailValid = helpers.withMessage(
      "Værdien er ikke en gyldig e-mailadresse", // Custom Danish message
      email
    );

    const isEmailUniqueInContactsList = helpers.withMessage(
      "E-mailen findes allerede i kontaktlisten",
      (value: string) => {
        if (!value) return true;
        return !(application.emailContacts ?? []).some(
          (contact) => contact?.toLowerCase() === value.toLowerCase()
        );
      }
    );

    const rules = {
      newEmail: { email: isEmailValid, isEmailUniqueInContactsList },
    };

    const emailValidation$ = useVuelidate(rules, {
      newEmail,
    });

    function onPrimaryButton() {
      v$.value.$touch();
      if (!v$.value.$error)
        if (application.applicationId) {
          updateMutation.call(application);
        } else {
          createMutation.call(application);
        }
    }

    function onDelete(event: Event) {
      if (application.applicationId)
        confirmService.show(
          event,
          `Er du sikker? Handlingen kan ikke fortrydes`,
          "Ja, slet",
          "Afbryd",
          () => {
            deleteMutation.call(application.applicationId ?? "");
          }
        );
    }

    function reset() {
      isVisible.value = false;
      v$.value.$reset();
      Object.assign(application, {});
    }

    createMutation.onDone((result) => {
      toastService.show({
        severity: "success",
        title: "Applikation oprettet",
        message: `Den har fået ID: ${result.data?.createApplication?.applicationId}`,
      });

      reset();
    });

    updateMutation.onDone(() => {
      toastService.show({
        severity: "success",
        title: "Applikationen opdateret",
        message: "Dine ændringer er blevet gemt",
      });

      reset();
    });

    deleteMutation.onDone(() => {
      toastService.show({
        severity: "success",
        title: "Applikation slettet",
        message: "Applikationen er blevet slettet",
      });
      router.push("/applications");
    });

    createMutation.onError((error) =>
      toastService.show({ severity: "error", error: useError(error) })
    );
    updateMutation.onError((error) =>
      toastService.show({ severity: "error", error: useError(error) })
    );

    deleteMutation.onError((error) =>
      toastService.show({ severity: "error", error: useError(error) })
    );

    // Watch for modal visibility and focus input when it becomes visible
    watch(isVisible, (newValue) => {
      if (newValue) {
        nextTick(() => {
          // Try multiple ways to access and focus the input
          const inputEl =
            nameInput.value?.$el?.querySelector("input") ||
            nameInput.value?.$el ||
            nameInput.value?.$refs?.input ||
            nameInput.value;

          if (inputEl && typeof inputEl.focus === "function") {
            inputEl.focus();
          }
        });
      }
    });

    function handleInputEmail() {
      emailValidation$.value.newEmail.$touch();
    }

    function onAddEmailContacts() {
      application.emailContacts?.push(newEmail.value);
      newEmail.value = "";
    }

    function onDeleteEmailContacts(data: any) {
      application.emailContacts?.splice(
        application.emailContacts.indexOf(data),
        1
      );
    }

    return {
      isVisible,
      nameInput,
      application,
      v$,
      isLoading: computed(
        () => createMutation.loading.value || updateMutation.loading.value
      ),
      show: (value: ApplicationType | null) => {
        Object.assign(application, {
          ...(value ?? {}),
          emailContacts: [...(value?.emailContacts ?? [])],
        });
        isVisible.value = true;
      },
      hide: () => reset(),
      onPrimaryButton,
      onDelete,
      deleteMutation,
      emailValidation$,
      newEmail,
      handleInputEmail,
      onAddEmailContacts,
      onDeleteEmailContacts,
    };
  },
});

function useValidations() {
  return {
    name: { required },
  };
}
